import React from 'react';
import useMapParams from '../../../hooks/useMapParams';
import { useTranslation } from 'react-i18next';

import NestedCommoditySelector from '../NestedCommoditySelector';
import { StatusDropdown } from '../home/WeatherMapControls';
import useCommodityParam from '../../../hooks/useCommodityParam';
import { STATUS_VALUES, parseStatusParam, setStatusParam } from '../../../utils/mapUrlParams';
import AnalyticsChartFullscreenButton from '../analytics/AnalyticsChartFullscreenButton';
import PartySelect from './PartySelect';

/**
 * Persistent global filter strip. Single source of truth for commodity (single-select)
 * and status (multi-select) on the Global Produce Map. Both bind to URL params so deep
 * links from Analytics scorecards work and refresh preserves state.
 * Party selects (importer always; exporter in Shipments) are render-only — options
 * come from the active response once the active response carries them.
 */
const GlobalFilterStrip = ({
  regions = [],
  isFullscreen = false,
  onEnterFullscreen,
  onExitFullscreen,
  showFullscreen = true,
  // 'stacked' (default) = the Analytics filter-bar look, shared by every strip
  // in the app (unfilled 32px control, #E2E2E2 border, 12px text) — the Status
  // dropdown beside it uses the matching variant. 'floating' is the legacy Map
  // look (label inside the control, #B1B9B4 border), kept for callers that
  // still want it.
  commodityVariant = 'stacked',
  // Lets the mobile top-bar mount size the commodity control to the hamburger
  // button next to it. Default keeps the 32px filter-row height.
  commodityHeightClass,
  partyReady = false,
  importer = '',
  importerOptions = [],
  onImporterChange,
  showExporter = false,
  exporter = '',
  exporterOptions = [],
  onExporterChange,
}) => {
  const { t } = useTranslation();
  const [commodity, setCommodity] = useCommodityParam();
  const [searchParams, setSearchParams] = useMapParams();

  // Default = all statuses when param absent or invalid (avoids silent filtering;
  // matches the default applied inside UnifiedMapContainer).
  const parsed = parseStatusParam(searchParams);
  const visibleStatuses = parsed.length > 0 ? parsed : STATUS_VALUES;

  const handleStatusChange = (next) => setStatusParam(searchParams, setSearchParams, next);

  return (
    // Mobile (<lg): only the commodity selector and the fullscreen toggle live
    // in this row — Status moves next to the map controls inside
    // UnifiedMapContainer's mobile "Map controls" panel instead (avoids a
    // second dropdown crowding this already-tight row next to the hamburger).
    // Commodity is first in DOM order (top-left, beside the hamburger) and
    // the fullscreen button's `ml-auto` pushes it to the top-right of the
    // same row once Status is hidden. Desktop (lg+): Status is restored via
    // `lg:block`, exactly the original three-item layout.
    <div className="relative isolate z-[1300] flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
      {/* The mobile top-bar mount (the only caller passing a height) sizes to
          its content beside the hamburger; the desktop strip keeps its own
          fixed column width. */}
      <div className={commodityHeightClass ? 'w-fit max-w-full min-w-[180px]' : 'min-w-[180px] sm:min-w-[200px]'}>
        <NestedCommoditySelector
          variant={commodityVariant}
          {...(commodityHeightClass ? { heightClass: commodityHeightClass } : {})}
          value={commodity}
          onChange={setCommodity}
          placeholder={t('map.commodityPlaceholder', 'All commodities')}
        />
      </div>

      <div className="hidden lg:block">
        <StatusDropdown
          variant="stacked"
          regions={regions}
          visibleStatuses={visibleStatuses}
          onStatusesChange={handleStatusChange}
        />
      </div>

      {/* Shown on mobile too when partyReady — saved/URL party filters still
          hit the active request, so hiding the control would trap users. */}
      {partyReady ? (
        <PartySelect
          label={t('map.filters.importer', 'Importer')}
          value={importer}
          options={importerOptions}
          onChange={onImporterChange}
          allLabel={t('map.filters.allImporters', 'All')}
        />
      ) : null}

      {partyReady && showExporter ? (
        <PartySelect
          label={t('map.filters.exporter', 'Exporter')}
          value={exporter}
          options={exporterOptions}
          onChange={onExporterChange}
          allLabel={t('map.filters.allExporters', 'All')}
        />
      ) : null}

      {showFullscreen && onEnterFullscreen && onExitFullscreen ? (
        <div className="ml-auto">
          <AnalyticsChartFullscreenButton
            compact
            heightClass="h-10"
            isActive={isFullscreen}
            onEnter={onEnterFullscreen}
            onExit={onExitFullscreen}
          />
        </div>
      ) : null}
    </div>
  );
};

export default GlobalFilterStrip;
